// Application-wide configuration and constants
// Centralize URLs, assets, routes, and external service endpoints to minimize hardcoding

export const appConfig = {
  site: {
    brandName: 'AMIVA',
    organizationName: 'AMIVA Equity Partners',
    baseUrl: 'https://www.amivaplus.com',
    appUrl: 'https://app.amivaplus.com',
    signInUrl: 'https://deal-ai.net/auth/signin',
    contactEmail: 'info@amivaequitypartners.com',
    supportEmail: 'support@amivaequity.com',
    contactPhone: '+1 (914) 690-6661',
    address: {
      street: '641 Lexington Ave 15th fl',
      city: 'New York',
      state: 'NY',
      zip: '10022',
      country: 'United States',
    },
    locales: ['en_US'],
    socialLinks: [
      'https://www.linkedin.com/in/mike-pantouvakis-9062b914b/',
      'https://www.linkedin.com/company/28809728/',
      'https://www.linkedin.com/company/95702380/',
    ],
  },

  assets: {
    logoBlack: 'https://storage.googleapis.com/react-images/amiva-black-logo.png',
    logoWhite: 'https://storage.googleapis.com/react-images/AMIVAwhiteLogo.png',
    defaultOgImage: 'https://storage.googleapis.com/react-images/amiva-og-default.png',
    favicon: 'https://storage.googleapis.com/react-images/AMIVAwhiteLogo.png',
  },

  documents: {
    termsUrl:
      'https://storage.googleapis.com/amiva_public_files/AMIVA%2B%2C%20Inc.%20-%20Terms%20of%20Use%20for%20www.app.amivaplus.com%20-%20Version%202.0.pdf',
  },

  api: {
    backendBaseUrl:
      (typeof process !== 'undefined' && process.env.REACT_APP_SERVER_URL) ||
      (typeof process !== 'undefined' && process.env.REACT_APP_API_URL) ||
      (typeof window !== 'undefined' && window.location && window.location.origin) ||
      `http://localhost:${(typeof process !== 'undefined' && process.env.PORT) || '3000'}`,
    openai: {
      model:
        (typeof process !== 'undefined' && process.env.REACT_APP_OPENAI_MODEL) ||
        'gpt-4o-mini',
      proxyEndpoint:
        (typeof process !== 'undefined' && process.env.REACT_APP_VALUATION_FUNCTION_URL) ||
        (typeof window !== 'undefined' &&
        window.location &&
        window.location.hostname === 'localhost' &&
        window.location.port === '3000'
          ? 'http://localhost:8888/.netlify/functions/valuation-openai'
          : '/.netlify/functions/valuation-openai'),
    },
    monday: {
      captureEndpoint:
        (typeof process !== 'undefined' && process.env.REACT_APP_MONDAY_FUNCTION_URL) ||
        (typeof window !== 'undefined' &&
        window.location &&
        window.location.hostname === 'localhost' &&
        window.location.port === '3000'
          ? 'http://localhost:8888/.netlify/functions/monday-capture'
          : '/.netlify/functions/monday-capture'),
    },
    paths: {
      reportGenerate: '/api/report/generate',
      createCheckout: '/api/create-checkout-session',
      health: '/api/health',
      purchaseStatus: (valuationId, query = '') => `/api/purchase-status/${valuationId}${query}`,
      downloadReport: (token) => `/api/download/report/${token}`,
    },
  },

  scraping: {
    userAgent: 'Mozilla/5.0 (compatible; BusinessAnalyzer/1.0)',
    timeoutMs: 8000,
    proxies: (
      [
        'https://api.allorigins.win/get?url=',
        'https://corsproxy.io/?',
        'https://cors-anywhere.herokuapp.com/',
      ]
    ),
  },

  // Feature flags for temporarily taking parts of the site offline.
  // Flip a flag back to `true` to restore that area everywhere it is referenced
  // (routes, header/footer navigation, internal links, and the sitemap).
  features: {
    // Team page is hidden for now; set to true to bring /about/team back.
    teamPage: false,
  },

  routes: {
    home: '/',
    about: '/about',
    valuation: '/valuation',
    valuationUpgrade: '/valuation/upgrade/:valuationId',
    valuationSuccess: '/valuation/success',
    valuationResult: '/valuation/result/:valuationId',
    report: '/report/:valuationId',
    team: '/about/team',
    process: '/process',
    successStories: '/about/success-stories',
    faq: '/about/faq',
    services: {
      businessValuation: '/services/business-valuation',
      buyerSourcing: '/services/buyer-sourcing',
    },
    industries: '/industries',
    industryPages: {
      dental: '/dentalpractice-mna-advisor',
      saas: '/saas-mna-advisor',
      manufacturing: '/manufacturing-precision-machining-mna',
      hvac: '/hvac-home-services-mna-advisor',
      commercialCleaning: '/commercial-cleaning-mna-advisor',
      crossBorder: '/cross-border-mna-us-europe',
    },
    privacyPolicy: '/privacy-policy',
  },

  payments: {
    priceIds: {
      professional: 'price_professional',
      exitReadiness: 'price_exit_readiness',
    },
  },

  links: {
    calendly: 'https://app.usemotion.com/meet/mike-pantouvakis/acquisition-discussion',
    linkedinProfile: 'https://www.linkedin.com/in/mikepantouvakis',
  },

  seo: {
    defaultKeywords: [
      'business valuation',
      'M&A advisory',
      'sell business',
      'business broker',
      'exit planning',
      'mergers and acquisitions',
      'business sale advisor',
      'sell my business fast',
      'business exit strategy',
      'boutique M&A firm',
    ],
    preconnect: [
      'https://fonts.googleapis.com',
      'https://storage.googleapis.com',
    ],
    titleSuffix: ' | AMIVA M&A Advisory',
    openGraph: {
      siteName: 'AMIVA',
      locale: 'en_US',
      twitterSite: '@AMIVAEquity',
      twitterCreator: '@AMIVAEquity',
    },
    hreflangLocales: [
      { code: 'en-US', baseUrl: 'https://www.amivaplus.com' },
      { code: 'en-GB', baseUrl: 'https://uk.amivaplus.com' },
      { code: 'en-CA', baseUrl: 'https://ca.amivaplus.com' },
      { code: 'en-AU', baseUrl: 'https://au.amivaplus.com' },
    ],
  },

  sitemap: {
    routes: [
      { path: '/', changefreq: 'weekly', priority: 1.0 },
      { path: '/valuation', changefreq: 'weekly', priority: 0.9 },
      { path: '/about', changefreq: 'monthly', priority: 0.6 },
      { path: '/about/team', changefreq: 'monthly', priority: 0.6, feature: 'teamPage' },
      { path: '/process', changefreq: 'monthly', priority: 0.6 },
      { path: '/about/success-stories', changefreq: 'monthly', priority: 0.6 },
      { path: '/about/faq', changefreq: 'monthly', priority: 0.5 },
      { path: '/services/business-valuation', changefreq: 'monthly', priority: 0.7 },
      { path: '/services/buyer-sourcing', changefreq: 'monthly', priority: 0.7 },
      { path: '/industries', changefreq: 'monthly', priority: 0.6 },
      { path: '/dentalpractice-mna-advisor', changefreq: 'monthly', priority: 0.7 },
      { path: '/saas-mna-advisor', changefreq: 'monthly', priority: 0.7 },
      { path: '/manufacturing-precision-machining-mna', changefreq: 'monthly', priority: 0.7 },
      { path: '/hvac-home-services-mna-advisor', changefreq: 'monthly', priority: 0.7 },
      { path: '/commercial-cleaning-mna-advisor', changefreq: 'monthly', priority: 0.7 },
      { path: '/cross-border-mna-us-europe', changefreq: 'monthly', priority: 0.7 },
      { path: '/privacy-policy', changefreq: 'yearly', priority: 0.2 },
    ],
  },
};

export const getCanonicalUrl = (path = '/') => {
  const base = appConfig.site.baseUrl.replace(/\/$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized}`;
};
